import { useCallback, useEffect, useState } from 'react';
import { FlatList, Image, RefreshControl, Text, View } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import {
  PendingIdentityVerification,
  listPendingIdentityVerifications,
  resolveIdentityVerification,
  getAdminIdentityPhotoToken,
} from '../api/admin';
import { Button, Card, EmptyState, ErrorText, FormInput, Label, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';
import { adminIdentityPhotoUrl } from '../config';

const ROLE_LABELS: Record<string, string> = {
  REVIEWER: 'Người nhận việc',
  SHOP_OWNER: 'Người đăng việc',
};

/** Fetches its own short-lived photo token — a session-wide token can't be
 * embedded directly in the image URL (see backend JwtPhotoStrategy). */
function AdminIdentityPhoto({ token, userId }: { token: string; userId: string }) {
  const [photoToken, setPhotoToken] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getAdminIdentityPhotoToken(token, userId)
      .then((r) => {
        if (!cancelled) setPhotoToken(r.token);
      })
      .catch(() => {
        if (!cancelled) setPhotoToken(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, userId]);
  if (!photoToken) return null;
  return (
    <Image
      source={{ uri: adminIdentityPhotoUrl(userId, photoToken) }}
      style={{ width: 220, height: 140, borderRadius: 8, marginTop: 10 }}
      resizeMode="contain"
    />
  );
}

export default function AdminIdentityVerificationsScreen() {
  const { token } = useAuth();
  const [items, setItems] = useState<PendingIdentityVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setItems(await listPendingIdentityVerifications(token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Không tải được danh sách xác minh danh tính, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onResolve = async (userId: string, approve: boolean) => {
    if (!token) return;
    if (!approve && !notes[userId]?.trim()) {
      setError('Vui lòng nhập lý do từ chối');
      return;
    }
    setError(null);
    setBusyId(userId);
    try {
      await resolveIdentityVerification(token, userId, approve, notes[userId]);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Xử lý thất bại');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ScreenContainer>
      <ErrorText>{error}</ErrorText>
      <FlatList
        data={items}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
        ListEmptyComponent={!loading && !error ? <EmptyState text="Không có yêu cầu xác minh danh tính nào đang chờ" /> : null}
        renderItem={({ item }) => (
          <Card>
            <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text }}>{item.name}</Text>
            <Text style={{ color: colors.textMuted, marginTop: 2 }}>{item.email}</Text>
            <Text style={{ color: colors.primary, marginTop: 2, fontSize: 12, fontFamily: fonts.semiBold }}>
              {ROLE_LABELS[item.role] ?? item.role}
            </Text>
            <Text style={{ color: colors.text, marginTop: 6, fontSize: 13 }}>
              Số CCCD/CMND đã khai: {item.identityIdNumber ?? '—'}
            </Text>

            {item.identityPhotoUrl && token ? <AdminIdentityPhoto token={token} userId={item.id} /> : null}

            <Text style={{ color: colors.textMuted, marginTop: 8, fontSize: 11 }}>
              Gửi lúc: {new Date(item.createdAt).toLocaleString('vi-VN')}
            </Text>

            <View style={{ marginTop: 12 }}>
              <Label>Lý do từ chối (nếu từ chối)</Label>
              <FormInput
                value={notes[item.id] ?? ''}
                onChangeText={(v) => setNotes((n) => ({ ...n, [item.id]: v }))}
                placeholder="Ví dụ: ảnh mờ, số CCCD không khớp"
              />
              <Button title="Duyệt" onPress={() => onResolve(item.id, true)} loading={busyId === item.id} />
              <Button
                title="Từ chối"
                variant="danger"
                onPress={() => onResolve(item.id, false)}
                loading={busyId === item.id}
              />
            </View>
          </Card>
        )}
      />
    </ScreenContainer>
  );
}
